import { useEffect } from 'react';
import Link from 'next/link';
import {
  Card,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useOutletProducts } from '@/src/modules/outlets/hooks/use-outlet-products';
import { toTitleCase } from '../../utils/common';
import AnyError from '../produk-kesehatan/AnyError';
import SearchNotFound from '../produk-kesehatan/SearchNotFound';
import { ProdukSkeleton } from './ProdukSkeleton';

type produkProps = {
  outlet_id: number;
  offset: number;
  size: number;
  latitude?: number | undefined;
  longitude?: number | undefined;
  totalData?: (total: number, per_page: number) => void;
  search?: string;
  goToProduk?: () => void;
  showHeader?: boolean;
};

export function Produk({
  outlet_id,
  offset,
  size,
  latitude,
  longitude,
  totalData,
  search,
  goToProduk,
  showHeader,
}: produkProps) {
  const ITEMS_PER_PAGE = 4;

  const { data, error, isLoading } = useOutletProducts({
    outlet_id,
    offset,
    size,
    latitude,
    longitude,
    search,
  });

  useEffect(() => {
    if (data?.meta?.total !== undefined && totalData) {
      totalData(data.meta.total, data.meta.per_page_size);
    }
  }, [data?.meta?.total]);

  const isEmpty = !isLoading && data?.data.length === 0;

  const renderContent = () => {
    if (showHeader && (isEmpty || search || error)) {
      return null;
    }

    if (isEmpty && search) {
      return <SearchNotFound />;
    }

    if (isEmpty) {
      return <SearchNotFound />;
    }

    if (error) {
      return <AnyError />;
    }

    if (data?.data.length === 0) {
      return null;
    }

    return (
      <Stack>
        {showHeader && (
          <Group justify="space-between" mt="lg">
            <Title order={3} fz={{ base: 'lg', md: 'xl', lg: '1.5rem' }}>
              Produk Rekomendasi
            </Title>

            <Text
              variant="subtle"
              fz={{ base: 'xs', md: 'lg' }}
              c="#026D77"
              onClick={goToProduk}
              style={{ cursor: 'pointer' }}
            >
              Lihat Semua
            </Text>
          </Group>
        )}

        <SimpleGrid
          cols={{ base: 2, sm: 4 }}
          spacing={{ base: 'sm', lg: 'lg' }}
        >
          {isLoading
            ? Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                <ProdukSkeleton key={i} />
              ))
            : data?.data.map((item) => {
                const isSamePrice = item.price_range === item.final_price_range;

                return (
                  <Card
                    key={item.id}
                    padding="0"
                    radius="md"
                    mt="md"
                    component={Link}
                    href={`/detail-item/product/${item.id}`}
                    className="cursor-pointer"
                    withBorder
                  >
                    <Image
                      src={item.image}
                      alt={item.product_name}
                      h={{ base: '140', sm: '220' }}
                      w="100%"
                      fit="contain"
                      fallbackSrc="/images/logo.png"
                      className="rounded-t-md"
                    />

                    <Stack
                      gap="0.1rem"
                      my="sm"
                      align="flex-start"
                      px={{ base: 'sm', md: 'md' }}
                      justify="space-between"
                    >
                      <Stack gap={5}>
                        <Text lineClamp={2} fz="sm" fw={500}>
                          {toTitleCase(item.product_name)}
                        </Text>
                        <Text fw="bold" fz="sm" lineClamp={2}>
                          {item.final_price_range}
                        </Text>
                        {!isSamePrice && (
                          <Text
                            c="dimmed"
                            td="line-through"
                            fz="xs"
                            lineClamp={1}
                          >
                            {item.price_range}
                          </Text>
                        )}
                      </Stack>
                    </Stack>
                  </Card>
                );
              })}
        </SimpleGrid>
      </Stack>
    );
  };

  return <Stack>{renderContent()}</Stack>;
}
